import type { ComponentProps, VoidComponent } from "solid-js";
import { createMemo, mergeProps, Show, splitProps } from "solid-js";
import type { IconName, IconProps, IconType } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import type { ClassList } from "../../types";
import { isNil } from "lodash-es";
import type { TranslationKey } from "../../state/i18n";
import { useTranslation } from "../../state/i18n";
import { keyFromSelector } from "i18next";

type GenericButtonProps = {
  size?: Size;
  disabled?: boolean;
  classes?: string;
  loading?: boolean;
  block?: boolean;
  classList?: ClassList;
} & ComponentProps<"button"> &
  (
    | { variant?: "primary" | "secondary"; color?: "destructive" }
    | { variant?: "tertiary"; color?: "brand" | "destructive" }
    | { variant?: "light"; color?: unknown }
  );

type Size = "large" | "medium" | "small";

type ButtonProps = {
  content:
    | { variant: "label"; text: string; status?: "none" | "success" | "warning" | "danger" | "neutral" }
    | { variant: "labelAndIcon"; text: string; icon: IconName }
    | { variant: "icon"; icon: IconName; iconType?: IconType; class?: string };
} & GenericButtonProps;

const statusTranslations: Record<string, TranslationKey> = {
  success: keyFromSelector(($) => $.STATUS_INDICATION_SUCCESS),
  warning: keyFromSelector(($) => $.STATUS_INDICATION_WARNING),
  danger: keyFromSelector(($) => $.STATUS_INDICATION_DANGER),
  neutral: keyFromSelector(($) => $.STATUS_INDICATION_NEUTRAL),
};

const Button: VoidComponent<ButtonProps> = (props) => {
  const { t } = useTranslation();
  const [localProps, otherProps] = splitProps(props, [
    "size",
    "variant",
    "color",
    "disabled",
    "loading",
    "block",
    "content",
    "class",
    "classList",
  ]);

  const merged = mergeProps(
    {
      size: "large",
      variant: "primary",
      disabled: false,
      loading: false,
      block: false,
      classList: {},
    },
    localProps,
  );

  const iconSize = createMemo<IconProps["size"]>(() => {
    if (localProps.content.variant === "icon") {
      return merged.size === "large" || merged.size === "medium" ? "md" : "xs";
    } else {
      return merged.size === "large" ? "md" : "sm";
    }
  });

  const iconClasses = createMemo(() => ({
    "icon-button-icon-primary-default group-aria-expanded/button:icon-button-icon-primary-default group-aria-disabled/button:icon-button-icon-primary-disabled":
      merged.variant === "primary",
    "icon-button-icon-secondary-default group-aria-expanded/button:icon-button-icon-secondary-default group-aria-disabled/button:icon-button-icon-secondary-disabled":
      merged.variant === "secondary" && merged.color !== "destructive",
    "icon-button-icon-secondary-destructive group-aria-expanded/button:icon-button-icon-secondary-destructive group-aria-disabled/button:icon-button-icon-secondary-disabled":
      merged.variant === "secondary" && merged.color === "destructive",
    "icon-button-icon-tertiary-harsh group-aria-expanded/button:icon-button-icon-tertiary-active group-aria-disabled/button:icon-button-icon-tertiary-disabled":
      merged.variant === "tertiary" && merged.color !== "destructive" && merged.color !== "brand",
    "icon-button-icon-secondary-destructive group-aria-expanded/button:icon-button-icon-secondary-destructive group-aria-disabled/button:icon-button-icon-tertiary-disabled":
      merged.variant === "tertiary" && merged.color === "destructive",
    "icon-button-icon-tertiary-default group-aria-expanded/button:icon-button-icon-tertiary-default group-aria-disabled/button:icon-button-icon-tertiary-disabled":
      merged.variant === "tertiary" && merged.color === "brand",
    "icon-button-icon-primary-harsh group-active/button:icon-button-icon-primary-default group-aria-expanded/button:icon-button-icon-primary-default group-aria-disabled/button:icon-button-icon-primary-disabled":
      merged.variant === "light",
  }));

  const Spinner = () => (
    <Icon name="spinner" size={iconSize()} type="solid" class="animate-spin" classList={iconClasses()} />
  );

  const content = createMemo(() => {
    switch (merged.content.variant) {
      case "label":
        return {
          content: merged.loading ? (
            <>
              <Spinner />
              {merged.content.text}
            </>
          ) : (
            <>
              <Show when={!isNil(merged.content.status) && merged.content.status !== "none"}>
                <svg
                  aria-label={!isNil(merged.content.status) ? t(statusTranslations[merged.content.status]) : ""}
                  width="8"
                  height="8"
                  viewBox="0 0 8 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="mr-2"
                  classList={{
                    "icon-status-success": merged.content.status === "success",
                    "icon-status-warning": merged.content.status === "warning",
                    "icon-status-danger": merged.content.status === "danger",
                    "icon-status-neutral": merged.content.status === "neutral",
                  }}
                >
                  <circle cx="4" cy="4" r="4" fill="currentColor" />
                </svg>
              </Show>
              {merged.content.text}
            </>
          ),
          label: merged.content.text,
        };
      case "labelAndIcon":
        return {
          content: (
            <>
              {merged.loading ? (
                <Spinner />
              ) : (
                <Icon name={merged.content.icon} type="solid" size={iconSize()} classList={iconClasses()} />
              )}
              {merged.content.text}
            </>
          ),
          text: merged.content.text,
        };
      case "icon":
        return {
          content: merged.loading ? (
            <Spinner />
          ) : (
            <Icon
              name={merged.content.icon}
              type={merged.content.iconType ?? "solid"}
              size={iconSize()}
              classList={!isNil(merged.content.class) ? { [merged.content.class]: true } : iconClasses()}
            />
          ),
          label: merged.content.icon,
        };
    }
  });

  return (
    <button
      aria-label={content().label}
      aria-disabled={merged.disabled || merged.loading}
      data-loading={merged.loading}
      disabled={merged.disabled || merged.loading}
      class={merged.class}
      classList={{
        ...{
          "group/button": true,
          "pointer-events-none": merged.disabled || merged.loading,
          "flex items-center justify-center overflow-hidden rounded-sm select-none focus-visible:outline-default": true,
          "w-9.5 px-0!": merged.content.variant === "icon" && merged.size === "large",
          "w-7.5 px-0!": merged.content.variant === "icon" && merged.size === "medium",
          "w-6 px-0!": merged.content.variant === "icon" && merged.size === "small",
          "flex gap-x-1.5":
            merged.content.variant === "labelAndIcon" || (merged.content.variant === "label" && merged.loading),
          "w-full": merged.block,
          "shrink-0": !merged.block,
          "h-6": merged.size === "small",
          "h-7.5 px-2.5 text-button-small": merged.size === "medium",
          "h-9.5 px-4 text-button whitespace-nowrap": merged.size === "large",
          "pl-2": merged.content.variant === "labelAndIcon" && merged.size === "medium",
          "pl-3": merged.content.variant === "labelAndIcon" && merged.size === "large",
          "bg-button-bg-primary-default text-button-text-primary-enabled hover:bg-button-bg-primary-hover active:bg-button-bg-primary-pressed disabled:bg-button-bg-primary-disabled disabled:text-button-text-primary-disabled aria-expanded:bg-button-bg-primary-active":
            merged.variant === "primary" && merged.color !== "destructive",
          "bg-button-bg-primary-destructive-default text-button-text-primary-enabled hover:bg-button-bg-primary-destructive-hover active:bg-button-bg-primary-destructive-pressed disabled:bg-button-bg-primary-destructive-disabled disabled:text-button-text-primary-disabled aria-expanded:bg-button-bg-primary-destructive-active":
            merged.variant === "primary" && merged.color === "destructive",
          "border border-button-border-secondary bg-button-bg-secondary-default text-button-text-secondary-enabled hover:border-button-border-secondary-hover hover:bg-button-bg-secondary-hover active:border-button-border-secondary-pressed active:bg-button-bg-secondary-pressed disabled:border-0 disabled:bg-button-bg-secondary-disabled disabled:text-button-text-secondary-disabled aria-expanded:border-button-border-secondary-active aria-expanded:bg-button-bg-secondary-active":
            merged.variant === "secondary" && merged.color !== "destructive",
          "border border-button-border-secondary bg-button-bg-secondary-default text-button-text-secondary-destructive hover:border-button-border-secondary-hover hover:bg-button-bg-secondary-hover active:border-button-border-secondary-pressed active:bg-button-bg-secondary-pressed disabled:border-0 disabled:bg-button-bg-secondary-disabled disabled:text-button-text-secondary-disabled aria-expanded:border-button-border-secondary-active aria-expanded:bg-button-bg-secondary-active":
            merged.variant === "secondary" && merged.color === "destructive",
          "bg-button-bg-tertiary-default text-button-text-tertiary-harsh hover:bg-button-bg-tertiary-hover active:bg-button-bg-tertiary-pressed disabled:bg-button-bg-tertiary-disabled disabled:text-button-text-tertiary-disabled aria-expanded:bg-button-bg-tertiary-active":
            merged.variant === "tertiary" && merged.color !== "destructive" && merged.color !== "brand",
          "bg-button-bg-tertiary-default text-button-text-tertiary-default hover:bg-button-bg-tertiary-hover active:bg-button-bg-tertiary-pressed disabled:bg-button-bg-tertiary-disabled disabled:text-button-text-tertiary-disabled aria-expanded:bg-button-bg-tertiary-active":
            merged.variant === "tertiary" && merged.color === "brand",
          "bg-button-bg-tertiary-default text-button-text-tertiary-destructive hover:bg-button-bg-tertiary-hover active:bg-button-bg-tertiary-pressed disabled:bg-button-bg-tertiary-disabled disabled:text-button-text-tertiary-disabled aria-expanded:bg-button-bg-tertiary-active":
            merged.variant === "tertiary" && merged.color === "destructive",
          "bg-button-bg-primary-light-default text-button-text-primary-harsh hover:bg-button-bg-primary-light-hover active:bg-button-bg-primary-light-pressed active:text-button-text-primary-enabled disabled:bg-button-bg-primary-light-disabled disabled:text-button-text-primary-disabled aria-expanded:bg-button-bg-primary-light-active aria-expanded:text-button-text-primary-enabled":
            merged.variant === "light",
        },
        ...merged.classList,
      }}
      type="button"
      {...otherProps}
    >
      {content().content}
    </button>
  );
};

// Icon Button
export type IconButtonProps = {
  icon: IconName;
  iconType?: IconType;
  iconClass?: string;
  "aria-label": string;
  size?: Size;
} & Omit<GenericButtonProps, "block">;

export const IconButton: VoidComponent<IconButtonProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, ["icon", "iconType", "iconClass"]);
  return (
    <Button
      content={{ variant: "icon", icon: localProps.icon, iconType: localProps.iconType, class: localProps.iconClass }}
      title={props["aria-label"]}
      // Cast needed: splitProps widens the `variant`/`color` correlated union in `otherProps`.
      {...(otherProps as GenericButtonProps & { children?: undefined })}
    />
  );
};

// Text and Icon Button
export type TextAndIconButtonProps = {
  text: string;
  icon: IconName;
  size?: Extract<Size, "large" | "medium">;
} & GenericButtonProps;

export const TextAndIconButton: VoidComponent<TextAndIconButtonProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, ["text", "icon"]);
  return <Button content={{ variant: "labelAndIcon", text: localProps.text, icon: localProps.icon }} {...otherProps} />;
};

// Text Button
export type TextButtonProps = {
  text: string;
  size?: Extract<Size, "large" | "medium">;
  status?: "none" | "success" | "warning" | "danger" | "neutral";
} & GenericButtonProps;

export const TextButton: VoidComponent<TextButtonProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, ["text", "status"]);
  return <Button content={{ variant: "label", text: localProps.text, status: localProps.status }} {...otherProps} />;
};

type InputButtonProps = {
  icon: IconName;
  classList?: ClassList;
} & ComponentProps<"button">;

export const InputButton: VoidComponent<InputButtonProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, ["icon", "classList"]);

  return (
    <Button
      content={{ variant: "icon", icon: localProps.icon }}
      size="medium"
      variant="light"
      classList={{ ...localProps.classList, "size-7!": true }}
      {...otherProps}
    />
  );
};
